import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, MonitorSmartphone, Monitor, Smartphone, Share } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import OfficeModalHeader from './office/OfficeModalHeader';
import './PwaInstallModal.css';
import { useLanguage } from '../context/LanguageContext';
import { ensureNotifyServiceWorker } from '../lib/chatNotify';
import {
  applyPwaBrand,
  canUseNativeInstallPrompt,
  dismissPwaPrompt,
  getInstallPlatform,
  installPwaCapture,
  isStandalonePwa,
  promptNativeInstall,
  shouldOfferPwaInstall,
} from '../lib/pwaInstall';

function hasOtherOpenDialog() {
  return typeof document !== 'undefined' && Array.from(
    document.querySelectorAll('[role="dialog"], [role="alertdialog"]'),
  ).some((node) => node.getAttribute('data-state') !== 'closed'
    && node.getAttribute('data-testid') !== 'pwa-install-modal');
}

/**
 * Pop-up pós-login: instala o Office como app (PWA) no Windows, Android e iOS.
 */
export default function PwaInstallModal({
  brandName = 'Dragon Tech',
  logoUrl = '',
  pwaIconUrl = '',
  themeColor = '',
  openGate = true,
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [dialogBlocked, setDialogBlocked] = useState(hasOtherOpenDialog);
  const [installing, setInstalling] = useState(false);
  const [nativeReady, setNativeReady] = useState(canUseNativeInstallPrompt);
  const [done, setDone] = useState(false);
  const platform = useMemo(() => getInstallPlatform(), []);
  const currentDevice = platform === 'ios' ? 'ios' : platform === 'android' ? 'android' : 'desktop';
  const [device, setDevice] = useState(currentDevice);
  const [feedback, setFeedback] = useState('');
  const iconSrc = pwaIconUrl || logoUrl || '/pwa-icon-192.png';

  useEffect(() => {
    const update = () => setDialogBlocked(hasOtherOpenDialog());
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['role', 'data-state', 'data-testid'],
    });
    update();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    installPwaCapture();
    void ensureNotifyServiceWorker();
    applyPwaBrand({
      brand_name: brandName,
      theme_color: themeColor,
      pwa_icon_url: pwaIconUrl || logoUrl || '',
    });
  }, [brandName, themeColor, pwaIconUrl, logoUrl]);

  useEffect(() => {
    const onReady = () => setNativeReady(canUseNativeInstallPrompt());
    const onInstalled = () => {
      setDone(true);
      setOpen(false);
    };
    window.addEventListener('aurax:pwa-installable', onReady);
    window.addEventListener('aurax:pwa-installed', onInstalled);
    return () => {
      window.removeEventListener('aurax:pwa-installable', onReady);
      window.removeEventListener('aurax:pwa-installed', onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!openGate) return undefined;
    if (isStandalonePwa() || !shouldOfferPwaInstall()) return undefined;

    // Atraso curto para não competir com o primeiro paint / outros modais.
    const timer = window.setTimeout(() => {
      if (!shouldOfferPwaInstall()) return;
      setOpen(true);
    }, 1800);

    return () => window.clearTimeout(timer);
  }, [openGate]);

  useEffect(() => {
    const onForceOpen = () => {
      if (isStandalonePwa()) return;
      setNativeReady(canUseNativeInstallPrompt());
      setOpen(true);
    };
    window.addEventListener('aurax:open-pwa-install', onForceOpen);
    return () => window.removeEventListener('aurax:open-pwa-install', onForceOpen);
  }, []);

  const closeLater = useCallback(() => {
    dismissPwaPrompt({ never: false });
    setOpen(false);
  }, []);

  const closeNever = useCallback(() => {
    dismissPwaPrompt({ never: true });
    setOpen(false);
  }, []);

  const onInstall = useCallback(async () => {
    if (platform === 'ios') {
      // iOS não tem beforeinstallprompt — as instruções já estão no corpo.
      return;
    }
    setInstalling(true);
    try {
      const result = await promptNativeInstall();
      if (result === 'accepted') {
        setDone(true);
        setOpen(false);
        return;
      }
      setFeedback(t(result === 'dismissed' ? 'pwa.cancelled' : 'pwa.manualFallback'));
    } finally {
      setNativeReady(canUseNativeInstallPrompt());
      setInstalling(false);
    }
  }, [platform, t]);

  const host =
    typeof window !== 'undefined' ? window.location.host.replace(/^www\./, '') : '';

  const nativeForDevice = nativeReady && device === currentDevice && platform !== 'ios';
  const steps = device === 'ios'
    ? ['pwa.guideIos1', 'pwa.guideIos2', 'pwa.guideIos3']
    : device === 'android'
      ? ['pwa.guideAndroid1', 'pwa.guideAndroid2', 'pwa.guideAndroid3']
      : ['pwa.guideDesktop1', 'pwa.guideDesktop2', 'pwa.guideDesktop3'];

  if (isStandalonePwa()) return null;

  return (
    <Dialog
      open={open && openGate && !dialogBlocked}
      onOpenChange={(next) => {
        if (!next) closeLater();
        else setOpen(true);
      }}
    >
      <DialogContent className="office-form-dialog pwa-install-dialog" data-testid="pwa-install-modal">
        <OfficeModalHeader icon={MonitorSmartphone} title={t('pwa.title')} description={t('pwa.simpleSubtitle', { brand: brandName })} />
        <div className="office-form-body pwa-install-body">
          <div className="pwa-install-identity">
            <img src={iconSrc} alt="" />
            <div><strong>{brandName}</strong><span>{host}</span></div>
            <span className="pwa-install-tag">{t('pwa.webApp')}</span>
          </div>
          <p className="pwa-install-intro">{t('pwa.simpleBenefit')}</p>
          <div className="pwa-install-devices" role="group" aria-label={t('pwa.chooseDevice')}>
            {[['desktop', 'pwa.computer', Monitor], ['android', 'pwa.android', Smartphone], ['ios', 'pwa.ios', Smartphone]].map(([key, label, Icon]) => (
              <button type="button" key={key} aria-pressed={device === key} onClick={() => setDevice(key)}><Icon size={16} aria-hidden />{t(label)}</button>
            ))}
          </div>
          <section className="pwa-install-guide" aria-labelledby="pwa-install-guide-title">
            <h3 id="pwa-install-guide-title">{t(device === 'ios' ? 'pwa.guideIosTitle' : device === 'android' ? 'pwa.guideAndroidTitle' : 'pwa.guideDesktopTitle')}</h3>
            {nativeForDevice && <p className="pwa-install-ready"><Download size={16} aria-hidden />{t('pwa.nativeReady')}</p>}
            <ol>{steps.map((key, index) => <li key={key}><span className="pwa-step-number" aria-hidden>{index + 1}</span><span>{t(key, { brand: brandName })}</span>{device === 'ios' && index === 0 && <Share size={16} aria-hidden />}</li>)}</ol>
          </section>
          {feedback && <p className="pwa-install-feedback" role="status">{feedback}</p>}
          <p className="pwa-install-note">{t('pwa.reopenHint')}</p>
        </div>
        <div className="office-dialog-footer pwa-install-footer">
          <Button type="button" variant="secondary" onClick={closeNever} disabled={installing}>{t('pwa.never')}</Button>
          {nativeForDevice ? <Button type="button" onClick={onInstall} disabled={installing || done} data-testid="pwa-install-btn"><Download size={16} aria-hidden />{installing ? t('pwa.installing') : t('pwa.installNow')}</Button>
            : <Button type="button" onClick={closeLater} data-testid="pwa-got-it-btn">{t('pwa.gotIt')}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
